import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Palette, Rocket, PartyPopper } from 'lucide-react';
import { TypeAnimation } from 'react-type-animation';
import MorphingButton from '../shared/MorphingButton';

const WelcomeScreen = ({ onNext, user }) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'clamp(400px, 60vh, 500px)',
        padding: 'clamp(30px, 8vw, 60px) clamp(20px, 5vw, 40px)',
        textAlign: 'center',
        background: '#FFFFFF'
      }}
    >
      {/* Animated Icon */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.1
        }}
        style={{
          marginBottom: 'clamp(20px, 4vw, 32px)'
        }}
      >
        <div
          style={{
            width: 'clamp(80px, 15vw, 100px)',
            height: 'clamp(80px, 15vw, 100px)',
            borderRadius: 'clamp(16px, 3vw, 24px)',
            background: '#3B82F6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <svg width="clamp(40, 8vw, 50)" height="clamp(40, 8vw, 50)" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 11L12 14L22 4"
              stroke="#FFFFFF"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16"
              stroke="#FFFFFF"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </motion.div>

      {/* Welcome Text */}
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        style={{
          fontSize: 'clamp(24px, 6vw, 36px)',
          fontWeight: '700',
          color: '#111827',
          marginBottom: 'clamp(12px, 2vw, 16px)',
          lineHeight: '1.2',
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(8px, 2vw, 12px)',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}
      >
        Welcome to Fylo{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}!
        <PartyPopper size={Math.min(32, window.innerWidth * 0.06)} color="#3B82F6" />
      </motion.h1>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        style={{
          fontSize: 'clamp(15px, 3.5vw, 18px)',
          color: '#374151',
          marginBottom: 'clamp(32px, 6vw, 48px)',
          maxWidth: '500px',
          lineHeight: '1.6',
          padding: '0 16px'
        }}
      >
        <TypeAnimation
          sequence={[
            "Let's set up your account in just a few steps.",
            1000,
            "This will help us personalize your experience.",
            1000,
            "Ready to create amazing forms?",
            1000
          ]}
          wrapper="p"
          speed={60}
          repeat={Infinity}
          style={{ display: 'inline-block' }}
        />
      </motion.div>

      {/* Features Preview */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        style={{
          display: 'flex',
          gap: 'clamp(12px, 3vw, 24px)',
          marginBottom: 'clamp(32px, 6vw, 48px)',
          flexWrap: 'wrap',
          justifyContent: 'center',
          padding: '0 16px'
        }}
      >
        {[
          { icon: Zap, text: 'Quick Setup' },
          { icon: Palette, text: 'Personalized' },
          { icon: Rocket, text: 'Get Started Fast' }
        ].map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(6px, 1.5vw, 8px)',
              padding: 'clamp(10px, 2vw, 12px) clamp(16px, 3vw, 20px)',
              backgroundColor: '#FFFFFF',
              borderRadius: 'clamp(8px, 2vw, 12px)',
              border: '1px solid #E5E7EB',
              fontSize: 'clamp(13px, 2.5vw, 14px)',
              fontWeight: '500',
              color: '#374151'
            }}
          >
            <item.icon size={Math.min(20, window.innerWidth * 0.04)} color="#3B82F6" />
            {item.text}
          </motion.div>
        ))}
      </motion.div>

      {/* CTA Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.5 }}
      >
        <MorphingButton
          onClick={async () => {
            await new Promise(resolve => setTimeout(resolve, 500));
            onNext();
          }}
          showSuccessState={false}
        >
          Let's Get Started →
        </MorphingButton>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        style={{
          fontSize: 'clamp(12px, 2.5vw, 13px)',
          color: '#6B7280',
          marginTop: 'clamp(16px, 3vw, 24px)'
        }}
      >
        Takes less than 2 minutes
      </motion.p>
    </div>
  );
};

export default WelcomeScreen;